import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Upload } from 'lucide-react';
import { Search, Tools as ToolsIcon } from 'iconoir-react';

import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import {
  IGetToolsResponse,
  ITool,
  ToolStatus,
  ToolType,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useDeleteTool, useGetOpenTools, useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useDebounce } from 'hooks/use-debounce';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { cn } from 'lib/utils';
import { AddMcpServerModal } from './components/add-mcp-server-modal';
import { ConnectorDetailsModal } from './components/connector-details-modal';
import { OpenConnectorDetailsModal } from './components/open-connector-details-modal';
import { EditComposioToolModal } from './components/edit-composio-tool-modal';
import { EditOpenConnectorToolModal } from './components/edit-open-connector-tool-modal';
import { EditToolModal } from './components/edit-tool-modal';
import { ImportMcpServersModal } from './components/import-mcp-servers-modal';
import { BlocksToolsList } from './blocks-tools-list';
import { ToolsList } from './tools-list';
import type { ViewMode } from './tools-shared';
import { getApiErrorDetail } from 'utils/api-error';

import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import ConfigurationButton from 'components/configuration/ConfigurationButton';
import { isOpenConnectorEnabled } from 'config/feature-flags';
import {
  countReplacedComposioTools,
  mergePromotedConnectors,
  PROMOTED_CONNECTORS_PAGE_SIZE,
  selectPromotedConnectors,
} from './connectors-dictionary';
import { useEnabledConnectorNames } from './use-enabled-connectors';
type ToolTab = 'tool' | 'mcp-server' | 'composio' | 'open-connectors' | 'blocks-tools';
type ToolSourceTab = 'featured' | 'mine';

const TOOL_TABS: ToolTab[] = ['tool', 'mcp-server', 'composio', 'open-connectors', 'blocks-tools'];

const getTabFromSearchParams = (searchParams: URLSearchParams): ToolTab => {
  const tab = searchParams.get('tab');
  if (tab === 'open-connectors' && !isOpenConnectorEnabled) return 'composio';
  return TOOL_TABS.includes(tab as ToolTab) ? (tab as ToolTab) : 'composio';
};

const PAGE_SIZE_OPTIONS = [12, 24, 48];

/** Badge totals use total_count, so they stay correct past 100 tools. */
const SOURCE_COUNT_PAGE_SIZE = 1;

function useSourceCounts(toolType: ToolType, projectKey: string) {
  const featuredQuery = useGetTools({
    tool_type: toolType,
    page: 1,
    page_size: SOURCE_COUNT_PAGE_SIZE,
    project_key: projectKey,
    created_by: 'catalog',
  });
  const myToolsQuery = useGetTools({
    tool_type: toolType,
    page: 1,
    page_size: SOURCE_COUNT_PAGE_SIZE,
    project_key: projectKey,
    with_system_tool: false,
  });
  const featuredCount = featuredQuery.data?.total_count ?? 0;
  const myToolsCount = myToolsQuery.data?.total_count ?? 0;

  return {
    featuredCount,
    myToolsCount,
    total: featuredCount + myToolsCount,
  };
}

function useToolsFirstPage(
  toolType: 'open_connector' | 'composio',
  projectKey: string,
  source: 'featured' | 'mine',
) {
  return useGetTools({
    tool_type: toolType,
    page: 1,
    page_size: PROMOTED_CONNECTORS_PAGE_SIZE,
    project_key: projectKey,
    ...(source === 'featured' ? { created_by: 'catalog' } : { with_system_tool: false }),
  });
}

function isToolsPageSynced(
  data: IGetToolsResponse | undefined,
  page: number,
  pageSize: number,
): data is IGetToolsResponse {
  if (!data || data.page !== page) {
    return false;
  }

  return data.page_size == null || data.page_size === pageSize;
}

export function Tools() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY || true;

  const [activeTab, setActiveTab] = useState<ToolTab>(() => getTabFromSearchParams(searchParams));
  const [toolSource, setToolSource] = useState<ToolSourceTab>('featured');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('grid');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [blocksToolType, setBlocksToolType] = useState<'all' | ToolType>('all');
  const [blocksToolStatus, setBlocksToolStatus] = useState<'all' | ToolStatus>('all');
  const [toolToDelete, setToolToDelete] = useState<ITool | null>(null);
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [isAddMcpServerModalOpen, setIsAddMcpServerModalOpen] = useState(false);
  const [isAddComposioModalOpen, setIsAddComposioModalOpen] = useState(false);
  const [isAddOpenConnectorModalOpen, setIsAddOpenConnectorModalOpen] = useState(false);
  const [isImportMcpModalOpen, setIsImportMcpModalOpen] = useState(false);
  const [selectedConnectorTool, setSelectedConnectorTool] = useState<ITool | null>(null);
  const [selectedOpenConnectorTool, setSelectedOpenConnectorTool] = useState<ITool | null>(null);

  useEffect(() => {
    setActiveTab(getTabFromSearchParams(searchParams));
  }, [searchParams]);

  const selectedToolType =
    activeTab === 'tool'
      ? 'api'
      : activeTab === 'mcp-server'
        ? 'mcp_server'
        : activeTab === 'composio'
          ? 'composio'
          : activeTab === 'open-connectors'
            ? 'open_connector'
            : null;

  const isBlocksToolsTab = activeTab === 'blocks-tools';

  const toolsQuery = useGetTools(
    {
      tool_type: selectedToolType,
      page,
      page_size: pageSize,
      project_key: projectKey,
      search: debouncedSearch || undefined,
      ...(!isBlocksToolsTab && toolSource === 'featured' ? { created_by: 'catalog' } : {}),
      ...(!isBlocksToolsTab && toolSource === 'mine' ? { with_system_tool: false } : {}),
    },
    { enabled: !isBlocksToolsTab },
  );

  const connectorCounts = useSourceCounts('composio', projectKey);
  const mcpCounts = useSourceCounts('mcp_server', projectKey);
  const apiCounts = useSourceCounts('api', projectKey);
  const openConnectorCounts = useSourceCounts('open_connector', projectKey);
  const enabledConnectorNames = useEnabledConnectorNames();
  const catalogConnectors = useToolsFirstPage('open_connector', projectKey, 'featured');
  const myConnectors = useToolsFirstPage('open_connector', projectKey, 'mine');
  const catalogComposio = useToolsFirstPage('composio', projectKey, 'featured');
  const myComposio = useToolsFirstPage('composio', projectKey, 'mine');
  const promoted = useMemo(() => {
    const featured = selectPromotedConnectors(catalogConnectors.data?.tools, enabledConnectorNames);
    const featuredIds = new Set(featured.map((tool) => tool.id));
    // The mine query also returns catalog rows. Count those only on Featured.
    const mine = selectPromotedConnectors(myConnectors.data?.tools, enabledConnectorNames).filter(
      (tool) => !featuredIds.has(tool.id),
    );
    const composioFeatured = catalogComposio.data?.tools ?? [];
    const composioIds = new Set(composioFeatured.map((tool) => tool.id));
    const composioMine = (myComposio.data?.tools ?? []).filter((tool) => !composioIds.has(tool.id));

    return {
      featured,
      mine,
      composioFeatured,
      composioMine,
      replacedFeatured: countReplacedComposioTools(composioFeatured, featured),
      replacedMine: countReplacedComposioTools(composioMine, mine),
    };
  }, [
    enabledConnectorNames,
    catalogConnectors.data?.tools,
    myConnectors.data?.tools,
    catalogComposio.data?.tools,
    myComposio.data?.tools,
  ]);
  const activeSourceCounts =
    selectedToolType === 'mcp_server'
      ? mcpCounts
      : selectedToolType === 'api'
        ? apiCounts
        : selectedToolType === 'open_connector'
          ? openConnectorCounts
          : connectorCounts;

  const blocksToolsCountQuery = useGetOpenTools({
    tool_type: null,
    tool_status: null,
    page: 1,
    page_size: SOURCE_COUNT_PAGE_SIZE,
    project_key: projectKey,
  });

  const openToolsQuery = useGetOpenTools(
    {
      tool_type: blocksToolType === 'all' ? null : blocksToolType,
      tool_status: blocksToolStatus === 'all' ? null : blocksToolStatus,
      page,
      page_size: pageSize,
      project_key: projectKey,
    },
    { enabled: isBlocksToolsTab },
  );

  const deleteToolMutation = useDeleteTool();
  const currentQuery = isBlocksToolsTab ? openToolsQuery : toolsQuery;
  const currentData = currentQuery.data;
  const isListLoading =
    currentQuery.isPending ||
    currentQuery.isFetching ||
    !isToolsPageSynced(currentData, page, pageSize);

  const displayData = useMemo(() => {
    const synced = isToolsPageSynced(currentData, page, pageSize) ? currentData : undefined;
    if (!synced || activeTab !== 'composio') return synced;

    const connectors = toolSource === 'featured' ? promoted.featured : promoted.mine;
    const composioTools =
      toolSource === 'featured' ? promoted.composioFeatured : promoted.composioMine;
    // Merge into the full Composio list, then page it here, so the promoted
    // connectors count toward the page size instead of being added on top.
    const merged = mergePromotedConnectors(
      { ...synced, tools: composioTools, total_count: composioTools.length },
      connectors,
      debouncedSearch,
      1,
      composioTools,
    );
    const totalCount = merged.tools.length;
    return {
      ...merged,
      tools: merged.tools.slice((page - 1) * pageSize, page * pageSize),
      total_count: totalCount,
      total_pages: Math.max(1, Math.ceil(totalCount / pageSize)),
    };
  }, [currentData, page, pageSize, activeTab, toolSource, promoted, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTab, toolSource]);

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const userApiToolsCount = apiCounts.total;
  const userMcpServerCount = mcpCounts.total;
  const userComposioToolsCount =
    connectorCounts.total +
    promoted.featured.length +
    promoted.mine.length -
    promoted.replacedFeatured -
    promoted.replacedMine;
  const userOpenConnectorToolsCount = openConnectorCounts.total;
  const blocksToolsCount = blocksToolsCountQuery.data?.total_count ?? 0;
  const featuredToolsCount =
    activeSourceCounts.featuredCount +
    (selectedToolType === 'composio' ? promoted.featured.length - promoted.replacedFeatured : 0);
  const myToolsCount =
    activeSourceCounts.myToolsCount +
    (selectedToolType === 'composio' ? promoted.mine.length - promoted.replacedMine : 0);

  const tabs = useMemo(
    () =>
      [
        {
          value: 'composio' as const,
          label: t('CONNECTOR') || 'Connector',
          count: userComposioToolsCount,
        },
        {
          value: 'mcp-server' as const,
          label: t('MCP', 'MCP'),
          count: userMcpServerCount,
        },
        {
          value: 'tool' as const,
          label: t('API') || 'API',
          count: userApiToolsCount,
        },
        ...(isOpenConnectorEnabled
          ? [
              {
                value: 'open-connectors' as const,
                label: t('OPEN_CONNECTORS') || 'Open Connectors',
                count: userOpenConnectorToolsCount,
              },
            ]
          : []),
        // {
        //   value: 'blocks-tools' as const,
        //   label: t('BLOCKS_TOOLS') || 'Blocks Tools',
        //   count: blocksToolsCount,
        // },
      ] satisfies Array<{
        value: ToolTab;
        label: string;
        count: number;
      }>,
    [
      t,
      userApiToolsCount,
      userMcpServerCount,
      userComposioToolsCount,
      userOpenConnectorToolsCount,
      blocksToolsCount,
    ]
  );

  const showAddButton = activeTab !== 'blocks-tools' && (isAdmin);
  const showImportButton = activeTab === 'mcp-server' && showAddButton;
  const showLimitMessage =
    activeTab !== 'blocks-tools' &&
    !isAdmin &&
    ((activeTab === 'mcp-server' && userMcpServerCount >= 5) ||
      (activeTab === 'composio' && userComposioToolsCount >= 5) ||
      (activeTab === 'open-connectors' && userOpenConnectorToolsCount >= 5) ||
      (activeTab === 'tool' && userApiToolsCount >= 5));

  const handleTabChange = (value: string) => {
    const tab = value as ToolTab;
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleAddClick = () => {
    if (activeTab === 'mcp-server') {
      setIsAddMcpServerModalOpen(true);
      return;
    }

    if (activeTab === 'composio') {
      setIsAddComposioModalOpen(true);
      return;
    }

    if (activeTab === 'open-connectors') {
      setIsAddOpenConnectorModalOpen(true);
      return;
    }

    if (activeTab === 'tool') {
      setIsAddToolModalOpen(true);
    }
  };

  const handleOpenToolDetails = (tool: ITool) => {
    if (tool.type === 'composio') {
      setSelectedConnectorTool(tool);
      return;
    }

    if (tool.type === 'open_connector') {
      setSelectedOpenConnectorTool(tool);
      return;
    }

    navigate(`/tool-details/${encodeURIComponent(tool.id)}`, {
      state: { tool, fromTab: activeTab },
    });
  };

  const handleDeleteTool = async () => {
    if (!toolToDelete || !projectKey) return;

    try {
      const result = await deleteToolMutation.mutateAsync({
        toolId: toolToDelete.id,
        projectKey,
        isBlocksTool: false,
      });

      if (result.is_success === false) {
        throw new Error(result.message || result.detail || 'Failed to delete tool');
      }

      toast({
        variant: 'success',
        title: t('TOOL_DELETED') || 'Tool deleted',
        description:
          t('TOOL_REMOVED_SUCCESSFULLY', {
            name: toolToDelete.name,
            defaultValue: '{{name}} was removed successfully.',
          }),
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'open-tools'] }),
      ]);
      setToolToDelete(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED') || 'Delete failed',
        description:
          getApiErrorDetail(error) ?? (t('FAILED_TO_DELETE_TOOL') || 'Failed to delete tool.'),
      });
    }
  };

  return (
    <>
      <ConfigurationLayout>
        <div className="flex w-full min-w-0 flex-col gap-6 border-b border-grey-100 pb-6 dark:border-border">
          <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <ConfigurationHeader
              title={t('TOOLS') || 'Tools'}
              subtitle={
                t('TOOLS_PAGE_SUBTITLE', {
                  defaultValue:
                    'Connect APIs, MCP servers, and connectors to extend what your agents can do.',
                }) ||
                'Connect APIs, MCP servers, and connectors to extend what your agents can do.'
              }
              icon={
                <ToolsIcon
                  width={20}
                  height={20}
                  strokeWidth={1.75}
                  className="text-grey-900 dark:text-foreground"
                />
              }
            />

            {showAddButton || showImportButton || showLimitMessage ? (
              <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-3 sm:w-auto sm:shrink-0">
                {!showAddButton && showLimitMessage ? (
                  <span className="min-w-0 max-w-md text-sm text-muted-foreground">
                    {t('5_OF_5_MY_TOOLS_CREATED') ||
                      '5 of 5 My Tools created. Please, delete one to add more'}
                  </span>
                ) : null}

                {showImportButton ? (
                  <ConfigurationButton
                    variant="outline"
                    icon={<Upload className="h-4 w-4" />}
                    onClick={() => setIsImportMcpModalOpen(true)}
                  >
                    {t('IMPORT_FROM_JSON', 'Import from JSON')}
                  </ConfigurationButton>
                ) : null}

                {showAddButton ? (
                  <ConfigurationButton
                    icon={<Plus className="h-4 w-4" />}
                    onClick={handleAddClick}
                  >
                    {t('ADD_INTEGRATION') || 'Add Integration'}
                  </ConfigurationButton>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center p-1">
              <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('SEARCH_TOOLS') || 'Search tools...'}
              className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-heading shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-10 pb-8">
          <div className="flex w-full min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            <div className="inline-flex max-w-full shrink-0 flex-nowrap items-center gap-2 overflow-x-auto">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.value;

                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => handleTabChange(tab.value)}
                    className={cn(
                      'inline-flex h-10 shrink-0 items-center rounded-lg border px-3 text-sm font-normal leading-heading transition-colors',
                      isActive
                        ? 'border-grey-100 bg-grey-50 text-grey-900 dark:border-true-white/40 dark:bg-true-white/15 dark:text-true-white'
                        : 'border-transparent text-grey-500 hover:bg-grey-50 hover:text-grey-900 dark:text-true-white/75 dark:hover:bg-true-white/10 dark:hover:text-true-white'
                    )}
                  >
                    {tab.label}
                    <span
                      className={cn(
                        'ml-1.5 text-xs',
                        isActive
                          ? 'text-grey-500 dark:text-true-white/80'
                          : 'text-grey-500 dark:text-true-white/55'
                      )}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              className={cn(
                'flex w-full min-w-0 gap-3 lg:w-auto lg:shrink-0',
                activeTab === 'blocks-tools'
                  ? 'flex-col md:flex-row md:items-center md:justify-between lg:flex-row lg:flex-nowrap lg:items-center lg:justify-end'
                  : 'flex-col sm:flex-row sm:items-center sm:justify-end lg:flex-row lg:justify-end'
              )}
            >
              {activeTab === 'blocks-tools' ? (
                <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:flex md:w-auto md:flex-row lg:shrink-0">
                  <Select
                    value={blocksToolType}
                    onValueChange={(value) => {
                      setBlocksToolType(value as 'all' | ToolType);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-9 w-full min-w-0 md:w-[170px]">
                      <SelectValue placeholder={t('TOOL_TYPE') || 'Tool type'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('ALL_TYPES') || 'All types'}</SelectItem>
                      <SelectItem value="api">{t('API') || 'API'}</SelectItem>
                      <SelectItem value="mcp_server">{t('MCP_SERVER', 'MCP Server')}</SelectItem>
                      <SelectItem value="composio">{t('CONNECTOR') || 'Connector'}</SelectItem>
                      {isOpenConnectorEnabled ? (
                        <SelectItem value="open_connector">{t('OPEN_CONNECTORS') || 'Open Connectors'}</SelectItem>
                      ) : null}
                      <SelectItem value="webhook">{t('WEBHOOK') || 'Webhook'}</SelectItem>
                      <SelectItem value="graphql">{t('GRAPHQL') || 'GraphQL'}</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={blocksToolStatus}
                    onValueChange={(value) => {
                      setBlocksToolStatus(value as 'all' | ToolStatus);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-9 w-full min-w-0 md:w-[170px]">
                      <SelectValue placeholder={t('STATUS') || 'Status'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('ALL_STATUS') || 'All status'}</SelectItem>
                      <SelectItem value="active">{t('ACTIVE') || 'Active'}</SelectItem>
                      <SelectItem value="inactive">{t('INACTIVE') || 'Inactive'}</SelectItem>
                      <SelectItem value="deprecated">{t('DEPRECATED') || 'Deprecated'}</SelectItem>
                      <SelectItem value="maintenance">{t('MAINTENANCE') || 'Maintenance'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {activeTab !== 'blocks-tools' ? (
                <div
                  role="group"
                  aria-label={t('FILTER') || 'Filter'}
                  className="inline-flex h-10 shrink-0 items-center self-end rounded-lg border border-grey-100 bg-white p-1 sm:self-auto dark:border-border dark:bg-card"
                >
                  {(
                    [
                      {
                        value: 'featured' as const,
                        label: t('FEATURED') || 'Featured',
                        count: featuredToolsCount,
                      },
                      {
                        value: 'mine' as const,
                        label: t('MY_TOOLS') || 'My Tools',
                        count: myToolsCount,
                      },
                    ]
                  ).map((option) => {
                    const isActive = toolSource === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => setToolSource(option.value)}
                        className={cn(
                          'inline-flex h-8 shrink-0 items-center rounded-md px-3 text-sm font-normal leading-heading transition-colors',
                          isActive
                            ? 'bg-grey-50 text-grey-900 dark:bg-true-white/15 dark:text-true-white'
                            : 'text-grey-500 hover:text-grey-900 dark:text-true-white/75 dark:hover:text-true-white'
                        )}
                      >
                        {option.label}
                        <span
                          className={cn(
                            'ml-1.5 text-xs',
                            isActive
                              ? 'text-grey-500 dark:text-true-white/80'
                              : 'text-grey-500 dark:text-true-white/55'
                          )}
                        >
                          {option.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          {activeTab === 'blocks-tools' ? (
            <BlocksToolsList
              data={displayData}
              isLoading={isListLoading}
              isAdmin={isAdmin}
              view={view}
              projectKey={projectKey}
              onToolSelect={handleOpenToolDetails}
              onDeleteTool={setToolToDelete}
              isDeletingToolId={deleteToolMutation.isPending ? toolToDelete?.id : null}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          ) : (
            <ToolsList
              data={displayData}
              isLoading={isListLoading}
              view={view}
              activeTab={activeTab}
              onAddClick={handleAddClick}
              onImportClick={showImportButton ? () => setIsImportMcpModalOpen(true) : undefined}
              onToolSelect={handleOpenToolDetails}
              onDeleteTool={setToolToDelete}
              isDeletingToolId={deleteToolMutation.isPending ? toolToDelete?.id : null}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              projectKey={projectKey}
              isAdmin={isAdmin}
            />
          )}
        </div>
      </ConfigurationLayout>

      <AlertDialog open={!!toolToDelete} onOpenChange={(open) => !open && setToolToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('DELETE_TOOL') || 'Delete tool?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {toolToDelete
                ? t('REMOVE_TOOL_FROM_TENANT', {
                  name: toolToDelete.name,
                  defaultValue: 'This will permanently remove "{{name}}" from this tenant.',
                })
                : t('THIS_ACTION_CANNOT_BE_UNDONE') || 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteToolMutation.isPending}>
              {t('CANCEL') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTool} disabled={deleteToolMutation.isPending}>
              {deleteToolMutation.isPending ? t('DELETING') || 'Deleting...' : t('DELETE') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditToolModal mode="create" open={isAddToolModalOpen} onOpenChange={setIsAddToolModalOpen} />
      <AddMcpServerModal
        open={isAddMcpServerModalOpen}
        onOpenChange={setIsAddMcpServerModalOpen}
        projectKey={projectKey}
      />
      <ImportMcpServersModal
        open={isImportMcpModalOpen}
        onOpenChange={setIsImportMcpModalOpen}
        projectKey={projectKey}
      />
      <EditComposioToolModal
        mode="create"
        open={isAddComposioModalOpen}
        onOpenChange={setIsAddComposioModalOpen}
      />
      <EditOpenConnectorToolModal
        mode="create"
        open={isAddOpenConnectorModalOpen}
        onOpenChange={setIsAddOpenConnectorModalOpen}
      />
      <OpenConnectorDetailsModal
        tool={selectedOpenConnectorTool}
        projectKey={projectKey}
        onClose={() => setSelectedOpenConnectorTool(null)}
      />
      <ConnectorDetailsModal
        tool={selectedConnectorTool}
        projectKey={projectKey}
        onClose={() => setSelectedConnectorTool(null)}
      />
    </>
  );
}
